/**
 * Comparison.jsx — main page
 *
 * Handles: KPIHeader, KPISubHeader, session restore, Redux sync, project title bar.
 * All content delegated to <ComparisonView pid={pid} viewIds={viewIds} />.
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch }    from 'react-redux';
import { Navigate }                    from 'react-router-dom';

import KPIHeader            from '../../Header/KPIHeader';
import KPISubHeader         from '../../Header/SubHeader/KPISubHeader';
import ComparsionDefinitions from '../../Defintions/ComparsionDefinitions';
import ComparisonView       from '../../KPI/Comparison/ComparisonView';

import { getSession }        from '../../utils/session';
import { setProjectDetails } from '../../userSlice';

const Comparison = () => {
  const dispatch = useDispatch();

  // ── Redux ──────────────────────────────────────────────────────────────────
  const reduxEmail       = useSelector((state) => state.user.email);
  const reduxProjectName = useSelector((state) => state.user.projectName);
  const reduxPid         = useSelector((state) => state.user.projectId);
  const reduxViewIds     = useSelector((state) => state.user.viewIds);
  const isSuperuser      = useSelector(s => s.user.isSuperuser);
  
  const [email,       setEmail]       = useState(reduxEmail       || '');
  const [projectName, setProjectName] = useState(reduxProjectName || '');
  const [pid,         setPid]         = useState(reduxPid         || '');
  const [viewIds,     setViewIds]     = useState(reduxViewIds     || []);
  const [loading,     setLoading]     = useState(true);

  // ── Session restore ────────────────────────────────────────────────────────
  useEffect(() => {
    // Redux already has data (normal navigation) — use it directly
    if (reduxEmail && reduxPid) {
      setEmail(reduxEmail);
      setPid(reduxPid);
      setProjectName(reduxProjectName || '');
      setViewIds(reduxViewIds || []);
      setLoading(false);
      return;
    }

    // Fall back to sessionStorage (hard refresh / direct URL)
    const session = getSession();
    if (session?.email) {
      setEmail(session.email);
      setPid(session.projectId       || '');
      setProjectName(session.projectName    || '');
      setViewIds(session.viewIds         || []);

      dispatch(setProjectDetails({
        email:            session.email,
        projectName:      session.projectName,
        projectId:        session.projectId,
        organizationName: session.organizationName,
        roleId:           session.roleId,
        viewIds:          session.viewIds || [],
      }));
    }
    setLoading(false);
  }, [dispatch, reduxEmail, reduxPid, reduxProjectName, reduxViewIds]);

  // Keep in sync if Redux changes (user switches project without hard refresh)
  useEffect(() => {
    if (reduxPid)             setPid(reduxPid);
    if (reduxViewIds?.length) setViewIds(reduxViewIds);
  }, [reduxPid, reduxViewIds]);

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (loading) return null;
  if (!email)  return <Navigate to="/" replace />;

  return (
    <div>
      <div className="z-50">
        <KPIHeader />
        <KPISubHeader />
      </div>

      <div className="mt-32">
        {/* Project title bar */}
        <div className="px-6 py-2 flex items-center justify-between border-b border-gray-200 mb-3 bg-white rounded-lg shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 truncate max-w-xs md:max-w-md">
              {projectName}
            </h1>
             {isSuperuser && (
              <span className="text-xs font-bold bg-purple-100 text-purple-700 border border-purple-300 px-2 py-0.5 rounded-full">
                SUPERUSER
              </span>
            )}
          </div>
          <span className="bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 text-sm font-semibold px-4 py-2 rounded-full shadow-sm flex items-center">
            PID: {pid}
          </span>

        </div>

        {/* ── All content sections, view-gated inside ComparisonView ────────── */}
        <ComparisonView pid={pid} viewIds={viewIds} />
      </div>
    </div>
  );
};

export default Comparison;